import { Button, Col, CreateTableState, Grid, Typography } from 'gu-ui-kit'
import React, { useCallback, useEffect, useState } from 'react'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useTranslation } from 'react-i18next'
import { StatusBadge } from 'modules/AccountsMarketplace/Category/subcomponents/StatusBadge'
import styles from 'modules/AccountsMarketplace/Category/subcomponents/Table/hooks/table.module.scss'
import format from 'utils/dateFns/format'
import clsx from 'clsx'
import { useDispatch } from 'react-redux'
import formatMoney from 'utils/formatMoney'

export const useTableConfig = ({ setSorting }, data = []) => {
  const breakpoints = useBreakpoint()
  const { t } = useTranslation()
  const [columnVisibility, setColumnVisibility] = useState()
  const dispatch = useDispatch()

  const openCredentialsModal = useCallback((id) => {
    dispatch({
      type: 'MODAL/SHOW',
      payload: {
        modalType: 'accountMarketCredentials',
        modalProps: {
          title: t(
            'accounts_marketplace::category.account_credential_modal.title',
          ),
          id,
          shouldCloseOnOverlayClick: true,
        },
      },
    })
  }, [])

  useEffect(() => {
    if (!breakpoints.md) {
        setColumnVisibility({
          order_status: false,
          order_created_at: false,
          product_price: false,
          quantity: false,
          total_price: false,
          credentials: false,
        })
    } else if (!breakpoints.lg) {
        setColumnVisibility({
          order_status: false,
          order_created_at: false,
          credentials: false,
        })
    } else if (!breakpoints.xl) {
        setColumnVisibility({ order_status: false, order_created_at: false })
    } else if (!breakpoints.xxl) {
        setColumnVisibility({ order_status: false })
    } else {
        setColumnVisibility({})
    }
  }, [breakpoints])

  const tableConfig = {
    columns: [
      {
        id: 'product_name',
        header: () => (
          <Typography variant={'Body-20'} color={'Label-Primary'}>
            {t('accounts_marketplace::category.list.head.product_name')}
          </Typography>
        ),
        cell: ({
          row: {
            original: {
              product_name,
              order_status,
              product_price,
              quantity,
              total_price,
              order_created_at,
              product_currency,
              id,
            },
          },
        }) => {
          const leftColumn = (
            <Grid gap={12}>
              <Grid wrap={false} gap={16} alignItems={'center'}>
                <Typography variant={'Body-15'}>{product_name}</Typography>
              </Grid>
              {!breakpoints.lg && (
                <Grid
                  alignItems={'center'}
                  gap={8}
                  wrap={false}
                  justifyContent={'space-between'}
                >
                  <Button
                    disabled={order_status !== 'success'}
                    className={styles['table__button-load-description']}
                    variant={'primary'}
                    size={'s'}
                    onClick={() => openCredentialsModal(id)}
                  >
                    {t(
                      'accounts_marketplace::category.list.rows.load_description',
                    )}
                  </Button>
                </Grid>
              )}
              {!breakpoints.xxl && (
                <Grid alignItems={'center'} gap={8} wrap={true}>
                  <div>
                    <StatusBadge status={order_status} />
                  </div>
                  {!breakpoints.xl && (
                    <Typography variant={'Body-10'} color={'Label-Secondary'}>
                      {format(new Date(order_created_at), 'dd.MM.yyyy')}
                    </Typography>
                  )}
                </Grid>
              )}
            </Grid>
          )

          return breakpoints.md ? (
            <Grid fluid wrap={false} alignItems={'center'}>
              {leftColumn}
            </Grid>
          ) : (
            <Grid gap={16}>
              <Col s={'6'}>{leftColumn}</Col>
              <Col s={'6'} alignSelf={'start'}>
                <Grid direction={'column'}>
                  <table className={styles['table__product-info']}>
                    <tbody>
                      <tr>
                        <td>
                          <Typography
                            variant={'Body-10'}
                            color={'Label-Secondary'}
                          >
                            {t(
                              'accounts_marketplace::category.list.head.product_price',
                            )}
                          </Typography>
                        </td>
                        <td>
                          <Typography
                            variant={'Body-10'}
                            dangerouslySetInnerHTML={{
                              __html: formatMoney(
                                product_price,
                                product_currency,
                              ),
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography
                            variant={'Body-10'}
                            color={'Label-Secondary'}
                          >
                            {t(
                              'accounts_marketplace::category.list.head.quantity',
                            )}
                          </Typography>
                        </td>
                        <td>
                          <Typography variant={'Body-10'}>
                            {quantity}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography
                            variant={'Body-10'}
                            color={'Label-Secondary'}
                          >
                            {t(
                              'accounts_marketplace::category.list.head.total_price',
                            )}
                          </Typography>
                        </td>
                        <td>
                          <Typography
                            variant={'Body-10'}
                            dangerouslySetInnerHTML={{
                              __html: formatMoney(
                                total_price,
                                product_currency,
                              ),
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </Grid>
              </Col>
            </Grid>
          )
        },
        className: '',
      },
      {
        id: 'order_status',
        header: () => (
          <Typography variant={'Body-20'} color={'Label-Primary'}>
            {t('accounts_marketplace::category.list.head.order_status')}
          </Typography>
        ),
        className: '',
        cell: ({
          row: {
            original: { order_status },
          },
        }) => <StatusBadge status={order_status} />,
      },
      {
        id: 'order_created_at',
        header: () => (
          <Typography variant={'Body-20'}>
            {t('accounts_marketplace::category.list.head.order_created_at')}
          </Typography>
        ),
        className: '',
        cell: ({
          row: {
            original: { order_created_at },
          },
        }) => (
          <Typography variant={'Body-3'}>
            {format(new Date(order_created_at), 'dd.MM.yyyy')}
          </Typography>
        ),
      },
      {
        id: 'product_price',
        header: () => (
          <Typography variant={'Body-20'}>
            {t('accounts_marketplace::category.list.head.product_price')}
          </Typography>
        ),
        className: '',
        cell: ({
          row: {
            original: { product_price, product_currency },
          },
        }) => (
          <Typography
            variant={'Body-3'}
            dangerouslySetInnerHTML={{
              __html: formatMoney(product_price, product_currency),
            }}
          />
        ),
      },
      {
        id: 'quantity',
        header: () => (
          <Typography variant={'Body-20'}>
            {t('accounts_marketplace::category.list.head.quantity')}
          </Typography>
        ),
        className: '',
        cell: ({
          row: {
            original: { quantity },
          },
        }) => <Typography variant={'Body-3'}>{quantity}</Typography>,
      },
      {
        id: 'total_price',
        header: () => (
          <Typography variant={'Body-20'}>
            {t('accounts_marketplace::category.list.head.total_price')}
          </Typography>
        ),
        className: '',
        cell: ({
          row: {
            original: { total_price, product_currency },
          },
        }) => (
          <Typography
            variant={'Body-3'}
            dangerouslySetInnerHTML={{
              __html: formatMoney(total_price, product_currency),
            }}
          />
        ),
      },
      {
        id: 'credentials',
        header: () => (
          <Typography variant={'Body-20'} color={'Label-Primary'} />
        ),
        className: '',
        cell: ({
          row: {
            original: { id, order_status },
          },
        }) => (
          <Grid justifyContent={'end'}>
            <Button
              disabled={order_status !== 'success'}
              className={styles['table__button-load-description']}
              variant={'primary'}
              size={'s'}
              onClick={() => openCredentialsModal(id)}
            >
              {t('accounts_marketplace::category.list.rows.load_description')}
            </Button>
          </Grid>
        ),
      },
    ],
    data,
    state: {
      columnVisibility: { ...columnVisibility },
    },
    showHeader: data.length > 0,
    showFooter: false,
    className: clsx('margin-bottom-16', styles['table']),
    setSorting,
  }

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}

